import {
  NAV_MANIFEST,
  OFF_MENU_PAGES,
} from "@/lib/navigation/nav-manifest";
import { normalizeRouteKey } from "@/lib/navigation/page-directory";

/**
 * Lookups over the pages the navigation declares, derived from the same
 * manifest the header, the ⌘K index, and the assistants' destination lists are
 * built from — so there is no second list of routes to fall out of date.
 *
 * Deliberately context-free (no role, module, or plugin gating), which is what
 * makes it safe to import from client components: it answers "is this one of
 * the app's real pages" and "what is it called", not "may this user open it".
 * Server code that must only name pages a user can actually reach uses
 * `listNavigationDestinations` instead.
 */

function normalizePathname(href) {
  const pathname = String(href || "").split(/[?#]/, 1)[0];
  return pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
}

/** Every manifest entry with an in-app href, groups and sections included. */
function* manifestEntries() {
  function* walk(items) {
    for (const item of items || []) {
      // Groups have no href; "#" is a placeholder; external links leave the app.
      if (item?.href && item.href !== "#" && !item.external) yield item;
      yield* walk(item?.subItems);
    }
  }
  for (const menu of NAV_MANIFEST) yield* walk(menu.items);
  yield* walk(OFF_MENU_PAGES);
}

/**
 * The pathname of every in-app manifest href, `{workspaceId}` templates
 * included: what `scripts/check-nav-routes.mjs` matches against `app/`.
 */
export function listManifestPathnames() {
  return new Set(
    Array.from(manifestEntries(), (item) => normalizePathname(item.href)),
  );
}

let manifestPathnames = null;

function getManifestPathnames() {
  if (!manifestPathnames) {
    // "{workspaceId}" hrefs are per-studio templates with no static pathname.
    manifestPathnames = new Set(
      [...listManifestPathnames()].filter((pathname) => !pathname.includes("{")),
    );
  }
  return manifestPathnames;
}

/** True when `href`'s pathname (query/hash ignored) is a manifest-declared page. */
export function isManifestPagePath(href) {
  const pathname = normalizePathname(href);
  return pathname.startsWith("/") && getManifestPathnames().has(pathname);
}

let manifestPages = null;

/**
 * Route key → what the page is called. An href the manifest lists more than
 * once (under several menus) is one page: the first entry names it, and the
 * entry that declares a `heading` or `description` supplies them.
 */
function getManifestPages() {
  if (!manifestPages) {
    manifestPages = new Map();
    for (const item of manifestEntries()) {
      // A "#section" href is a part of a page, not a page with a heading.
      if (item.href.includes("#")) continue;
      const key = normalizeRouteKey(
        item.href.replace("{workspaceId}", ":workspaceId"),
      );
      const page = manifestPages.get(key) || { label: item.label };
      if (item.heading) page.heading = item.heading;
      if (item.description) page.description = item.description;
      manifestPages.set(key, page);
    }
  }
  return manifestPages;
}

/**
 * The manifest's name for the page at `href` (a pathname or a live href with a
 * real studio id): `{ label, heading?, description? }`, or null when the
 * manifest does not list it. `heading` is present only when the page's H1 is
 * not its label.
 */
export function getManifestPage(href) {
  return getManifestPages().get(normalizeRouteKey(href)) || null;
}
